<template>
  <div class="detail-page">
    <!-- Top Header -->
    <div class="top-header">
      <div class="user-info" v-if="memberInfo">
        <img :src="memberInfo.icon || '/logo.png'" alt="User" class="user-avatar">
        <span class="user-name">{{ memberInfo.nickname || memberInfo.username || 'Jane' }}</span>
      </div>
    </div>

    <!-- Content Section -->
    <div class="content-section" v-if="articleDetail">
      <!-- Price and Tags -->
      <div class="price-row">
        <div class="price-tags">
        </div>
        <div class="engagement-stats">
          <div class="stat-block">
            <div class="stat-number">{{ articleFootCount?.collectionCount || 0 }}</div>
            <div class="stat-label">Want to</div>
          </div>
          <div class="stat-block">
            <div class="stat-number">{{ articleFootCount?.readCount || 0 }}</div>
            <div class="stat-label">Browse</div>
          </div>
        </div>
      </div>

      <!-- Title -->
      <h1 class="detail-title">{{ articleDetail.title }}</h1>

      <!-- Description -->
      <p class="detail-description">{{ articleDetail.content }}</p>

      <!-- Tags -->
      <div class="hashtags" v-if="articleDetail.keywords">
        <span>#{{ articleDetail.keywords }}</span>
      </div>

      <!-- Date -->
      <div class="detail-date">{{ formatDateYMD(articleDetail.publishTime) }}</div>

      <!-- Image Carousel -->
      <div 
        class="image-carousel"
        @touchstart="isImageContent ? onTouchStart : null"
        @touchmove="isImageContent ? onTouchMove : null"
        @touchend="isImageContent ? onTouchEnd : null"
      >
        <template v-if="isImageContent">
          <img :src="currentImage" alt="Post" class="carousel-image">
          <div class="carousel-indicators" v-if="images.length > 1">
            <span 
              v-for="(img, index) in images" 
              :key="index"
              :class="['indicator-dot', { active: index === currentIndex }]"
              @click="goToIndex(index)"
            ></span>
          </div>
        </template>
        <template v-else-if="isVideoContent">
          <video 
            class="carousel-image video-player"
            :src="videoUrl"
            controls
            playsinline
            webkit-playsinline
            preload="metadata"
            :poster="articleDetail?.pic || ''"
          ></video>
        </template>
      </div>
    </div>

    <!-- Bottom Navigation Bar -->
    <div class="bottom-bar">
      <img src="@/assets/logo.png" style="height: 40px;" />
      <button class="open-btn" @click="onOpenClick">Open &gt;</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { updateMetaTags } from '@/utils/meta';

export default {
  name: 'PostDetail',
  data() {
    return {
      articleDetail: null,
      memberInfo: null,
      articleFootCount: null,
      currentIndex: 0,
      touchStartX: 0,
      touchDeltaX: 0,
      isSwiping: false
    };
  },
  computed: {
    userInfo() {
      return this.memberInfo;
    },
    postDetail() {
      return this.articleDetail;
    },
    images() {
      if (!this.articleDetail) return [];
      if (Number(this.articleDetail?.contentType) === 2) {
        return [this.articleDetail?.pic || '/logo.png'];
      }
      const images = [];
      // 主图
      if (this.articleDetail.pic) images.push(this.articleDetail.pic);
      // 专辑图：支持数组、单一字符串、逗号拼接字符串
      const album = this.articleDetail.albumPics;
      if (album) {
        let candidates = [];
        if (Array.isArray(album)) {
          candidates = album;
        } else if (typeof album === 'string') {
          candidates = album.split(/[，,]/);
        } else {
          candidates = [album];
        }
        candidates
          .map(v => (typeof v === 'string' ? v.trim() : v))
          .filter(v => !!v)
          .forEach(pic => {
            if (!images.includes(pic)) images.push(pic);
          });
      }
      return images.length > 0 ? images : ['/logo.png'];
    },
    currentImage() {
      return this.images[this.currentIndex];
    },
    isImageContent() {
      return Number(this.articleDetail?.contentType) === 1;
    },
    isVideoContent() {
      return Number(this.articleDetail?.contentType) === 2 && !!this.videoUrl;
    },
    videoUrl() {
      if (Number(this.articleDetail?.contentType) === 2) {
        const album = this.articleDetail?.albumPics;
        if (!album) return '';
        if (Array.isArray(album)) {
          return (album.find(Boolean) || '').toString().trim();
        }
        if (typeof album === 'string') {
          const first = album.split(/[，,]/).map(s => s.trim()).find(Boolean);
          return first || '';
        }
        return (album || '').toString();
      }
      return this.articleDetail?.videoUrl || this.articleDetail?.video || this.articleDetail?.mediaUrl || '';
    },
    formattedPrice() {
      if (!this.articleDetail || !this.articleDetail.price) return null;
      return this.articleDetail.price;
    },
    statusText() {
      const statusMap = {
        1: 'Published',
        2: 'Draft',
        3: 'Deleted'
      };
      return this.articleDetail?.status ? statusMap[this.articleDetail.status] : null;
    }
  },
  mounted() {
    this.fetchPostDetail();
  },
  methods: {
    async fetchPostDetail() {
      const articleId = this.$route.params.articleId;
      try {
        const response = await axios.get(`/article/detail/${articleId}`);
        
        if (response.data.code === 200 && response.data.data) {
          this.articleDetail = response.data.data.article;
          this.memberInfo = response.data.data.member;
          this.articleFootCount = response.data.data.articleFootCountVo;
          
          // 更新 Open Graph meta 标签
          this.updateMetaTags();
        }
      } catch (error) {
        console.error('Error fetching post detail:', error);
      }
    },
    updateMetaTags() {
      if (!this.articleDetail) return;
      
      const title = this.articleDetail.title || 'Post Detail';
      const description = this.articleDetail.content 
        ? this.articleDetail.content.substring(0, 200) 
        : 'Check out this post on Xigfor';
      const image = this.currentImage || this.articleDetail.pic || '/logo.png';
      const url = window.location.href;
      
      updateMetaTags({
        title,
        description,
        image,
        url,
        type: 'article'
      });
    },
    goBack() {
      this.$router.go(-1);
    },
    goToIndex(index) {
      if (!this.images.length) return;
      const total = this.images.length;
      this.currentIndex = ((index % total) + total) % total;
    },
    nextImage() {
      this.goToIndex(this.currentIndex + 1);
    },
    prevImage() {
      this.goToIndex(this.currentIndex - 1);
    },
    onTouchStart(e) {
      if (!e.touches || !e.touches.length) return;
      this.isSwiping = true;
      this.touchStartX = e.touches[0].clientX;
      this.touchDeltaX = 0;
    },
    onTouchMove(e) {
      if (!this.isSwiping || !e.touches || !e.touches.length) return;
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
    },
    onTouchEnd() {
      if (!this.isSwiping) return;
      const threshold = 40;
      if (this.touchDeltaX > threshold) {
        this.prevImage();
      } else if (this.touchDeltaX < -threshold) {
        this.nextImage();
      }
      this.isSwiping = false;
      this.touchStartX = 0;
      this.touchDeltaX = 0;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    },
    formatDateYMD(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    },
    onOpenClick() {
      const appStoreUrl = 'https://apps.apple.com/app/id1234567890';
      const androidApkUrl = 'https://example.com/app.apk';
      const ua = navigator.userAgent || '';
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isAndroid = /Android/i.test(ua);
      if (isIOS) {
        window.location.href = appStoreUrl;
      } else if (isAndroid) {
        window.location.href = androidApkUrl;
      } else {
        window.location.href = appStoreUrl;
      }
    }
  },
  watch: {
    '$route'(to, from) {
      if (to.params.articleId !== from.params.articleId) {
        this.fetchPostDetail();
      }
    },
    // 当图片变化时更新 meta 标签
    currentImage() {
      if (this.articleDetail) {
        this.updateMetaTags();
      }
    }
  }
};
</script>

<style scoped>
.detail-page {
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  padding-bottom: 76px;
}

.top-header {
  display: flex;
  align-items: center;
  padding: 12px 16px 0;
  background: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.user-name {
  font-size: 16px;
  color: #010117;
  font-weight: 500;
}

.header-right {
  cursor: pointer;
}

.menu-icon {
  font-size: 24px;
  color: #000;
  font-weight: bold;
}

.content-section {
  flex: 1;
  padding: 8px 16px 16px;
  background: white;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.price-tags {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price {
  font-size: 24px;
  font-weight: bold;
  color: #e53935;
}

.tag {
  background: #FF426B;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.engagement-stats {
  display: flex;
  gap: 8px;
  text-align: right;
}

.stat-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 56px;
}

.stat-number {
  font-size: 11px;
  font-weight: 700;
  color: #9B9CA5;
}

.stat-label {
  margin-top: 2px;
  font-size: 11px;
  color: #9B9CA5;
}

.detail-title {
  font-size: 16px;
  font-weight: bold;
  color: #010117;
  margin-bottom: 12px;
  line-height: 1.3;
}

.detail-description {
  font-size: 14px;
  color: #010117;
  line-height: 1.5;
  margin-bottom: 12px;
}

.hashtags {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.hashtags span {
  color: #133667;
  font-size: 14px;
}

.detail-date {
  font-size: 13px;
  color: #9397A1;
  margin-bottom: 16px;
}

.image-carousel {
  width: 100%;
  position: relative;
  background: #d3d2d1;
  border-radius: 12px;
  overflow: hidden;
}

.carousel-image {
  width: 100%;
  height: auto;
  display: block;
}

.video-player {
  height: 260px; /* 提高视频显示高度 */
  object-fit: cover;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  background: transparent;
  z-index: 2;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
}

.indicator-dot.active {
  background: rgba(0, 0, 0, 0.6);
}

.bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: linear-gradient(135deg, #ffeef1 0%, #fff5f7 100%);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.bottom-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-group {
  display: flex;
  gap: 12px;
}

.bar-icon {
  font-size: 20px;
  color: #888;
  cursor: pointer;
}

.logo-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.logo-x {
  font-size: 18px;
  font-weight: bold;
  color: #e53935;
  background: white;
  padding: 4px 6px;
  border-radius: 4px;
}

.logo-brand {
  font-size: 16px;
  font-family: 'Sansita', cursive;
  color: #ff6600;
  font-weight: bold;
}

.bottom-right {
  display: flex;
  align-items: center;
}

.open-btn {
  background: linear-gradient(135deg, #ff9800 0%, #ff4869 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.open-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

@media (max-width: 480px) {
  .price {
    font-size: 20px;
  }
  
  .detail-title {
    font-size: 18px;
  }
  
  .detail-description {
    font-size: 14px;
  }
}
</style>

